import 'src/styles/badge.css';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';

/**
 * StatusBadge — JSX wrapper over the shared `.status-badge` pill styles
 * (src/styles/badge.css). Defaults match the existing raw-className usage
 * (uppercase, regular size); the extras are opt-in so older UI is unchanged.
 *
 * @param {Object} props
 * @param {'success'|'error'|'warning'|'info'|'notice'|'default'} props.variant - Color variant
 * @param {'soft'|'tint'|'outline'} props.tone - soft = flat tinted chip (default);
 *   tint = legacy bordered look; outline = white chip for tinted surfaces
 * @param {'md'|'sm'} props.size - 'sm' renders the compact chip used in list items
 * @param {boolean} props.uppercase - Set false for sentence-case labels
 * @param {boolean} props.dot - Leading status dot in the variant color
 * @param {Object} props.icon - Optional leading FontAwesome icon
 */
const StatusBadge = ({
  variant,
  tone,
  size,
  uppercase,
  dot,
  icon,
  className,
  children,
  ...props
}) => (
  <span
    className={classNames(
      'status-badge',
      'status-badge--rounded',
      variant !== 'default' && `status-badge--${variant}`,
      tone !== 'tint' && `status-badge--${tone}`,
      {
        'status-badge--sm': size === 'sm',
        'status-badge--regular-case': !uppercase,
      },
      className
    )}
    {...props}
  >
    {dot && <span className='status-badge__dot' />}
    {icon && <FontAwesomeIcon icon={icon} />}
    {children}
  </span>
);

StatusBadge.propTypes = {
  variant: PropTypes.oneOf(['success', 'error', 'warning', 'info', 'notice', 'default']),
  tone: PropTypes.oneOf(['soft', 'tint', 'outline']),
  size: PropTypes.oneOf(['md', 'sm']),
  uppercase: PropTypes.bool,
  dot: PropTypes.bool,
  icon: PropTypes.object,
  className: PropTypes.string,
  children: PropTypes.node.isRequired,
};

StatusBadge.defaultProps = {
  variant: 'default',
  tone: 'soft',
  size: 'md',
  uppercase: true,
  dot: false,
  icon: null,
  className: '',
};

export default StatusBadge;
